import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { api, type Delivery, type Webhook } from '../api';
import { formatAgo, formatDate } from '../format';
import { CopyButton, Empty, PageHead, Pill, TableWrap } from '../ui';
import { useConfirmable } from '../useConfirmable';

export default function WebhookPage() {
  const [webhook, setWebhook] = useState<Webhook | null>(null);
  const [deliveries, setDeliveries] = useState<Delivery[]>([]);
  const [url, setUrl] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [testResult, setTestResult] = useState<string>('');
  const [confirmRotate, setConfirmRotate] = useState(false);
  const { run, dialog } = useConfirmable();

  const load = useCallback(async () => {
    try {
      const [current, log] = await Promise.all([api.webhook(), api.deliveries()]);
      setWebhook(current);
      setUrl(current.url ?? '');
      setEnabled(current.configured ? current.enabled : true);
      setDeliveries(log.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the webhook');
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      setWebhook(await run('set_webhook', (confirm) => api.setWebhook(url.trim(), enabled, confirm)));
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the endpoint');
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    setError('');
    try {
      await api.removeWebhook();
      setUrl('');
      setEnabled(true);
      setSecret('');
      setTestResult('');
      await load();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not remove the endpoint');
    } finally {
      setBusy(false);
    }
  };

  const rotate = async () => {
    setBusy(true);
    setError('');
    try {
      const rotated = await run('rotate_webhook_secret', (confirm) =>
        api.rotateWebhookSecret(confirm),
      );
      setWebhook(rotated);
      setSecret(rotated.signing_secret ?? '');
      setConfirmRotate(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not replace the secret');
    } finally {
      setBusy(false);
    }
  };

  const reveal = async () => {
    setBusy(true);
    setError('');
    try {
      const revealed = await run('reveal_webhook_secret', (confirm) =>
        api.revealWebhookSecret(confirm),
      );
      setSecret(revealed.signing_secret ?? '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not show the secret');
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async () => {
    setBusy(true);
    setError('');
    setTestResult('');
    try {
      const result = await api.testWebhook();
      setTestResult(
        result.delivered
          ? `ok:Your endpoint answered ${result.status}. A real delivery will look the same.`
          : `bad:${result.error || 'Your endpoint did not accept it.'}`,
      );
      // The test is logged as a delivery; show it next to the real ones.
      setDeliveries((await api.deliveries()).items);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the test');
    } finally {
      setBusy(false);
    }
  };

  const failing = deliveries.filter((delivery) => delivery.status === 'FAILED').length;

  return (
    <>
      {dialog}
      <PageHead
        title="Webhook"
        lead="We post a signed event when a generation finishes. The API stays the record — this only saves you the polling."
      />

      <div className="panel">
        <div className="panel-head">
          <h3>Endpoint</h3>
          <Pill
            status={
              !webhook?.configured ? 'not set' : webhook.enabled ? 'ACTIVE' : 'paused'
            }
          />
        </div>
        <form onSubmit={save}>
          <label htmlFor="url">URL</label>
          <input
            id="url"
            style={{ width: '100%' }}
            placeholder="https://your-server.example/hooks/generations"
            value={url}
            onChange={(event) => setUrl(event.target.value)}
          />
          <p className="muted small">
            https only, on a public address. We POST here when a generation finishes; the API is
            still the record, so you can always poll instead.
          </p>
          <p>
            <label>
              <input
                type="checkbox"
                style={{ minWidth: 'auto', marginRight: 8 }}
                checked={enabled}
                onChange={(event) => setEnabled(event.target.checked)}
              />
              Deliveries enabled
            </label>
          </p>
          {error ? <p className="error">{error}</p> : null}
          <div className="row">
            <button type="submit" disabled={busy || !url.trim()}>
              Save
            </button>
            {webhook?.configured ? (
              <>
                <button type="button" className="secondary" onClick={sendTest} disabled={busy}>
                  Send test event
                </button>
                <button type="button" className="secondary" onClick={remove} disabled={busy}>
                  Remove
                </button>
              </>
            ) : null}
          </div>
          {testResult ? (
            <div className={testResult.startsWith('ok:') ? 'notice good' : 'notice bad'}>
              <p className="small">{testResult.slice(testResult.indexOf(':') + 1)}</p>
            </div>
          ) : null}
        </form>
      </div>

      <div className="panel">
        <h3>Signing secret</h3>
        <p className="muted small">
          Every delivery carries <span className="mono">X-Timestamp</span> and{' '}
          <span className="mono">X-Signature</span>. The signature is{' '}
          <span className="mono">sha256=HMAC(secret, timestamp + "." + body)</span> — check it
          before trusting a call, and reject a timestamp that is not recent.
        </p>
        {secret ? (
          <div className="snippet">
            <pre className="mono">{secret}</pre>
            <CopyButton
              text={secret}
              label="Copy"
              className="secondary small"
            />
          </div>
        ) : (
          <button className="secondary" onClick={reveal} disabled={busy}>
            Show secret
          </button>
        )}
        <p className="muted small">
          It stays the same if you change the URL, so a verifier you have already deployed keeps
          working.
        </p>

        {confirmRotate ? (
          <div className="notice">
            <p>
              The current secret stops working immediately. Deliveries your verifier rejects are
              retried for about half an hour, so update it promptly — the generations themselves are
              unaffected either way.
            </p>
            <div className="row">
              <button onClick={rotate} disabled={busy}>
                Replace it
              </button>
              <button className="secondary" onClick={() => setConfirmRotate(false)} disabled={busy}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="secondary" onClick={() => setConfirmRotate(true)}>
            Replace secret
          </button>
        )}
      </div>

      <div className="panel">
        <h3>Recent deliveries</h3>
        {failing > 0 ? (
          <div className="notice">
            {failing} of the last {deliveries.length} deliveries failed. The generations themselves
            are unaffected — they are still there to fetch.
          </div>
        ) : null}
        {deliveries.length === 0 ? (
          <Empty title="Nothing sent yet.">
            The first delivery goes out when a generation finishes. Send a test event above to check
            your verifier before spending one.
          </Empty>
        ) : (
          <TableWrap>
            <table>
              <thead>
                <tr>
                  <th>Generation</th>
                  <th>Status</th>
                  <th className="num">Attempts</th>
                  <th>Your response</th>
                  <th>When</th>
                </tr>
              </thead>
              <tbody>
                {deliveries.map((delivery) => (
                  <tr key={delivery.id}>
                    <td className="mono small">
                      {delivery.generation_id === 0 ? 'test' : delivery.generation_id}
                    </td>
                    <td>
                      <Pill status={delivery.status} />
                    </td>
                    <td className="num">{delivery.attempts}</td>
                    <td className="small">
                      {delivery.last_status ? (
                        <span className={delivery.last_status >= 400 ? 'danger' : undefined}>
                          {delivery.last_status}
                        </span>
                      ) : null}{' '}
                      <span className="muted">{delivery.last_error ?? ''}</span>
                    </td>
                    <td
                      className="small"
                      title={formatDate(delivery.delivered_at ?? delivery.created_at)}
                    >
                      {formatAgo(delivery.delivered_at ?? delivery.created_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrap>
        )}
      </div>
    </>
  );
}
